import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IconType } from '@ng-icons/core';
import { ResizeableComponent } from '../../../design/resizeable/resizeable.component';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { Direction } from '@angular/cdk/bidi';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import {
  BACK,
  LOGO_ALT,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-route-planner-header',
  templateUrl: './route-planner-header.component.html',
  styleUrls: ['./route-planner-header.component.scss'],
  standalone: false
})
export class RoutePlannerHeaderComponent extends ResizeableComponent implements OnInit {
  @Output() onHeaderButtonClick: EventEmitter<void> = new EventEmitter<void>();

  @Input() hideInDesktop: boolean = false;
  @Input() displayBackground: boolean = false;
  @Input() title: string = 'תכנון מסלול';
  @Input() showLogo: boolean = false;
  /**
   * Icon for the header back/close/menu button. When omitted (undefined/null)
   * the button is NOT rendered at all — see the template's @if (icon) guard.
   * This avoids the "phantom button" issue where slide-up-cards without an
   * explicit icon used to render a 40x40 empty mat-icon-button at the
   * top-right corner of the dialog header (audit: "button with no accessible
   * name and no visual representation").
   */
  @Input() icon: IconType | null = null;
  /** When true, overlay focus trap moves initial focus to the back/header icon button (e.g. filter sheet). */
  @Input() focusInitialOnBack: boolean = false;
  /** When set, applied to the title &lt;h2&gt; for dialog aria-labelledby on design-slide-up-card. */
  @Input() dialogTitleElementId: string | null = null;
  /**
   * Accessible name for the header back/close icon button.
   * Defaults to the translated "Back" label; consumers can override per context
   * (e.g. "Close filter dialog"). The button has no visible text, so this is required
   * for screen-reader users.
   */
  @Input() backButtonAriaLabel: string | null = null;

  direction: Direction
  /** Translated fallback for backButtonAriaLabel — updated as language changes. */
  backButtonAriaLabelFallback: string = BACK.hebrew;
  /** Accessible name for the clickable logo (acts as a home link). */
  logoHomeAriaLabel: string = LOGO_ALT.hebrew;

  constructor(
    protected override readonly resizeService: ResizeService,
    private readonly router: Router,
    private readonly queryParamsService: QueryParamsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly languageService: LanguageService
  ) {
    super(resizeService);
  }

  override ngOnInit(): void {
    super.ngOnInit()
    this.initializeMultiLanguageTexts()

    // Check if there are query parameters that need to be processed
    const queryParams = this.queryParamsService.currentParams;
    if (queryParams && queryParams['tab']) {
      // Trigger tab change to initialize the correct form
      this.queryParamsService._tabChanged$.next(eTab[queryParams['tab']]);
    }
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe(language => {
      this.direction = language.direction;
      this.backButtonAriaLabelFallback = BACK[language.language];
      this.logoHomeAriaLabel = LOGO_ALT[language.language];
    })
  }

  /**
   * Resolved accessible name for the header back/close button — explicit input wins,
   * otherwise the current translation of `BACK`.
   */
  get resolvedBackButtonAriaLabel(): string {
    return this.backButtonAriaLabel?.trim() || this.backButtonAriaLabelFallback;
  }

  navigateHome() {
    this.router.navigateByUrl(
      `/plan?tab=${this.queryParamsService.currentParams['tab']}`,
      {
        replaceUrl: true,
        onSameUrlNavigation: 'reload',
      }
    );
  }

  navigateToWebsite() {
    window.open(environment.website_url, '_blank');
  }


}
